import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, UserRound } from "lucide-react";
import { adminListUsers, adminCreateUser, adminDeleteUser } from "@/lib/api";
import { adminInputCls, adminGoldBtnCls } from "./shared";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
}

export const AdminAccountsCard = ({ onAuthError }: { onAuthError: () => void }) => {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const me = (localStorage.getItem("r3-admin-email") || "").toLowerCase();

  const load = useCallback(() => {
    adminListUsers()
      .then((r) => setUsers(r.data.users))
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else setUsers([]);
      });
  }, [onAuthError]);

  useEffect(() => load(), [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminCreateUser(form);
      toast.success(`Admin account created for ${form.email}`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err: any) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error(err?.response?.data?.detail || "Could not create account");
    }
    setSaving(false);
  };

  const remove = async (u: AdminUser) => {
    if (!window.confirm(`Remove admin access for ${u.email}?`)) return;
    try {
      await adminDeleteUser(u.id);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (err: any) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error(err?.response?.data?.detail || "Could not remove account");
    }
  };

  return (
    <div className="border border-white/10 bg-charcoal p-6" data-testid="admin-accounts-card">
      <p className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-tight text-white">
        <UserRound className="h-5 w-5 text-gold" />
        Admin Accounts
      </p>
      <p className="mt-2 text-xs leading-relaxed text-neutral-500">
        Everyone listed here can sign in to this dashboard. You cannot remove your own account or
        the last remaining admin.
      </p>

      <ul className="mt-5 space-y-3" data-testid="admin-users-list">
        {users === null ? (
          <li className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
          </li>
        ) : (
          users.map((u) => (
            <li
              key={u.id}
              data-testid={`admin-user-row-${u.email}`}
              className="flex items-center justify-between gap-3 border border-white/10 bg-ink px-4 py-3"
            >
              <div>
                <p className="text-sm font-bold text-white">
                  {u.email}
                  {u.email === me && (
                    <span className="ml-2 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.15em] text-gold">
                      You
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-neutral-600">
                  {u.name || "—"} · added {new Date(u.created_at).toLocaleDateString()}
                </p>
              </div>
              {u.email !== me && (
                <button
                  data-testid={`admin-user-remove-${u.email}`}
                  onClick={() => remove(u)}
                  className="p-2 text-neutral-500 transition-colors duration-200 hover:text-red-400"
                  aria-label={`Remove ${u.email}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))
        )}
      </ul>

      <form data-testid="admin-user-form" onSubmit={create} className="mt-5 grid gap-3 sm:grid-cols-3">
        <input
          data-testid="admin-user-name-input"
          placeholder="Name (optional)"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className={adminInputCls}
        />
        <input
          data-testid="admin-user-email-input"
          type="email"
          required
          placeholder="Email *"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className={adminInputCls}
        />
        <input
          data-testid="admin-user-password-input"
          type="password"
          required
          placeholder="Password (8+ chars) *"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          className={adminInputCls}
        />
        <button data-testid="admin-user-create-btn" type="submit" disabled={saving} className={`${adminGoldBtnCls} sm:col-span-3 sm:w-auto sm:justify-self-start`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add Admin Account
        </button>
      </form>
    </div>
  );
};
